// Error repository for MoZo.Live error reporting system
import { supabase } from "../supabase";
import {
  ErrorLog,
  ErrorStatus,
  CreateErrorLogInput,
} from "../../types/error-logs";
import {
  validateErrorLogInput,
  validateSearchParams,
  validateErrorId,
} from "../validation/error-validation";

export class ErrorRepository {
  private tableName = "error_logs";

  /**
   * Create a new error log
   */
  async create(errorLog: CreateErrorLogInput): Promise<ErrorLog | null> {
    // Validate input
    const { isValid, errors, sanitizedInput } = validateErrorLogInput(errorLog);

    if (!isValid || !sanitizedInput) {
      console.error("Invalid error log input:", errors);
      return null;
    }

    // Use the sanitized input but remove additional_context and app_version if they exist
    const { additional_context, app_version, ...sanitizedData } = sanitizedInput;
    
    const { data, error } = await supabase
      .from(this.tableName)
      .insert({
        ...sanitizedData,
        status: ErrorStatus.UNRESOLVED,
      })
      .select()
      .single();

    if (error) {
      console.warn("Error logging failed (database schema issue):", error.message);
      return null;
    }

    return data as ErrorLog;
  }

  /**
   * Get error logs with pagination and filtering
   */
  async getErrorLogs(
    params: {
      page?: number;
      limit?: number;
      status?: ErrorStatus;
      userId?: string;
      errorType?: string;
      searchQuery?: string;
      fromDate?: string;
      toDate?: string;
      sortBy?: keyof ErrorLog;
      sortOrder?: "asc" | "desc";
    } = {}
  ) {
    // Validate and sanitize search parameters
    const sanitizedParams = validateSearchParams(params);

    // Extract parameters with defaults
    const {
      page = 1,
      limit = 20,
      status,
      userId,
      errorType,
      searchQuery,
      fromDate,
      toDate,
      sortBy = "created_at",
      sortOrder = "desc",
    } = sanitizedParams;
    let query = supabase
      .from(this.tableName)
      .select(
        "*, profiles!error_logs_user_id_fkey(username, email, display_name)",
        { count: "exact" }
      );

    // Apply filters
    if (status) {
      query = query.eq("status", status);
    }

    if (userId) {
      query = query.eq("user_id", userId);
    }

    if (errorType) {
      query = query.eq("error_type", errorType);
    }

    if (searchQuery) {
      // Use Supabase's safe filter methods instead of string interpolation to prevent SQL injection
      query = query.or(
        `error_message.ilike.%${encodeURIComponent(searchQuery)}%,` +
          `error_type.ilike.%${encodeURIComponent(searchQuery)}%,` +
          `component.ilike.%${encodeURIComponent(searchQuery)}%`
      );
    }

    if (fromDate) {
      query = query.gte("created_at", fromDate);
    }

    if (toDate) {
      query = query.lte("created_at", toDate);
    }

    // Apply pagination
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === "asc" });

    // Execute query
    const { data, error, count } = await query.range(from, to);

    if (error) {
      console.warn("Error logging failed (database schema issue):", error.message);
      return { data: [], count: 0, error };
    }

    return {
      data,
      count,
      totalPages: Math.ceil((count || 0) / limit),
      currentPage: page,
    };
  }

  /**
   * Get a single error log by ID
   */
  async getById(id: string) {
    // Validate ID
    if (!validateErrorId(id)) {
      console.error(`Invalid error log ID: ${id}`);
      return null;
    }

    const { data, error } = await supabase
      .from(this.tableName)
      .select(
        "*, profiles!error_logs_user_id_fkey(username, email, display_name)"
      )
      .eq("id", id)
      .single();

    if (error) {
      console.warn("Error logging failed (database schema issue):", error.message);
      return null;
    }

    return data as ErrorLog & { profiles: any };
  }

  /**
   * Update an error log
   */
  async update(id: string, updates: Partial<ErrorLog>) {
    const { data, error } = await supabase
      .from(this.tableName)
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.warn("Error logging failed (database schema issue):", error.message);
      return null;
    }

    return data as ErrorLog;
  }

  /**
   * Mark an error as resolved
   */
  async markAsResolved(id: string, resolvedBy: string, notes?: string) {
    return this.update(id, {
      status: ErrorStatus.RESOLVED,
      resolved_at: new Date().toISOString(),
      resolved_by: resolvedBy,
      resolution_notes: notes || null,
    });
  }

  /**
   * Mark an error as in progress
   */
  async markAsInProgress(id: string) {
    return this.update(id, {
      status: ErrorStatus.IN_PROGRESS,
    });
  }

  /**
   * Mark an error as ignored
   */
  async markAsIgnored(id: string, notes?: string) {
    return this.update(id, {
      status: ErrorStatus.IGNORED,
      resolution_notes: notes || null,
    });
  }

  /**
   * Get error statistics
   */
  async getStatistics() {
    const { data, error } = await supabase.rpc("get_error_statistics");

    if (error) {
      console.warn("Error logging failed (database schema issue):", error.message);
      return {
        counts: {
          unresolved_count: 0,
          in_progress_count: 0,
          resolved_count: 0,
          ignored_count: 0,
          last_24h_count: 0,
          last_7d_count: 0,
          last_30d_count: 0,
        },
        top_errors: [],
        top_components: [],
        user_errors: [],
      };
    }

    return data;
  }

  /**
   * Get error types for filtering
   */
  async getErrorTypes() {
    // Use a workaround for distinct since it's not properly typed in some versions
    const { data, error } = await supabase
      .from(this.tableName)
      .select("error_type")
      .order("error_type");

    if (error) {
      console.warn("Error logging failed (database schema issue):", error.message);
      return [];
    }

    return data.map((item: { error_type: string }) => item.error_type);
  }

  /**
   * Get components for filtering
   */
  async getComponents() {
    // Use a workaround for distinct since it's not properly typed in some versions
    const { data, error } = await supabase
      .from(this.tableName)
      .select("component")
      .order("component");

    if (error) {
      console.warn("Error logging failed (database schema issue):", error.message);
      return [];
    }

    return data
      .map((item: { component: string | null }) => item.component)
      .filter(Boolean);
  }
}

export const errorRepository = new ErrorRepository();
